import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { getSettings, updateSettings, getUsers, createUser, deleteUser } from '@/lib/api';
import api from '@/lib/api';
import { Settings, Users, Mail, Shield, Plus, Trash2, Save, Zap, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', first_name: '', last_name: '', role: 'assistant', password: 'demo123' });
  const [autoApprove, setAutoApprove] = useState(null);

  const DOC_TYPES = ['coi', 'w9', 'license', 'workers_comp', 'auto_insurance', 'umbrella', 'bond', 'safety_document', 'contract'];

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes, aaRes] = await Promise.all([
        getSettings(), getUsers(),
        api.get('/settings/auto-approve').catch(() => ({ data: null }))
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data.items || []);
      setAutoApprove(aaRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Auto-save auto-approve settings on any change
  const saveAutoApprove = useCallback(async (newSettings) => {
    try {
      await api.put('/settings/auto-approve', newSettings);
    } catch { /* silent save */ }
  }, []);

  const updateAA = (field, value) => {
    setAutoApprove(prev => {
      const updated = { ...prev, [field]: value };
      saveAutoApprove(updated);
      return updated;
    });
  };

  const toggleDocType = (docType) => {
    setAutoApprove(prev => {
      const current = prev?.auto_approve_document_types || [];
      const updated = current.includes(docType) ? current.filter(t => t !== docType) : [...current, docType];
      const newSettings = { ...prev, auto_approve_document_types: updated };
      saveAutoApprove(newSettings);
      return newSettings;
    });
  };

  const handleSaveSettings = async () => {
    try {
      await updateSettings(settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
  };

  const handleCreateUser = async () => {
    try {
      await createUser(newUser);
      toast.success('User created');
      setShowAddUser(false);
      setNewUser({ email: '', first_name: '', last_name: '', role: 'assistant', password: 'demo123' });
      load();
    } catch { toast.error('Failed to create user'); }
  };

  const handleDeleteUser = async (id) => {
    try {
      await deleteUser(id);
      toast.success('User deleted');
      load();
    } catch { toast.error('Failed to delete user'); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-80 rounded-xl" /></div>;

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your company settings and team</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general"><Settings className="mr-2 h-4 w-4" /> General</TabsTrigger>
          <TabsTrigger value="auto-approve"><Zap className="mr-2 h-4 w-4" /> Auto-Approve</TabsTrigger>
          <TabsTrigger value="team"><Users className="mr-2 h-4 w-4" /> Team</TabsTrigger>
          <TabsTrigger value="chase"><Mail className="mr-2 h-4 w-4" /> Chase Config</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Company Settings</CardTitle>
              <CardDescription>Configure your VendorSuite instance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-score on upload</Label>
                  <p className="text-xs text-muted-foreground">Automatically run compliance scoring when a document is uploaded</p>
                </div>
                <Switch checked={settings?.auto_score_on_upload} onCheckedChange={v => setSettings(p => ({...p, auto_score_on_upload: v}))} />
              </div>
              <Separator />
              <div>
                <Label>Expiration Warning Days</Label>
                <Input type="number" className="mt-1 max-w-[200px]" value={settings?.default_expiration_warning_days || 30}
                  onChange={e => setSettings(p => ({...p, default_expiration_warning_days: parseInt(e.target.value)}))} />
              </div>
              <Button onClick={handleSaveSettings}><Save className="mr-2 h-4 w-4" /> Save Settings</Button>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Notifications</CardTitle>
              <CardDescription>Email notification preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <NotificationToggle />
            </CardContent>
          </Card>
        </TabsContent>


        {/* Auto-Approve Tab */}
        <TabsContent value="auto-approve" className="space-y-4">
          {autoApprove && (
            <>
              <Card className="rounded-xl">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        <Zap className="inline mr-2 h-4 w-4" /> Global Auto-Approve
                      </CardTitle>
                      <CardDescription>Automatically approve AI extractions that meet your criteria. Disabled by default.</CardDescription>
                    </div>
                    <Switch data-testid="auto-approve-global-toggle" checked={autoApprove.auto_approve_enabled || false} onCheckedChange={v => updateAA('auto_approve_enabled', v)} />
                  </div>
                </CardHeader>
                {autoApprove.auto_approve_enabled && (
                  <CardContent className="space-y-6">
                    {/* Threshold */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label>Score Threshold Rule</Label>
                          <p className="text-xs text-muted-foreground">Only auto-approve for vendors above a compliance score threshold</p>
                        </div>
                        <Switch checked={autoApprove.auto_approve_threshold_enabled || false} onCheckedChange={v => updateAA('auto_approve_threshold_enabled', v)} />
                      </div>
                      {autoApprove.auto_approve_threshold_enabled && (
                        <div className="flex items-center gap-4 mt-3">
                          <Slider data-testid="auto-approve-threshold-slider" value={[autoApprove.auto_approve_threshold || 80]} onValueChange={v => updateAA('auto_approve_threshold', v[0])} min={0} max={100} step={5} className="flex-1" />
                          <span className="text-sm font-semibold tabular-nums w-12 text-right" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{autoApprove.auto_approve_threshold || 80}%</span>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* No flags only */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>No Compliance Flags Only</Label>
                        <p className="text-xs text-muted-foreground">Only auto-approve documents with zero compliance flags</p>
                      </div>
                      <Switch checked={autoApprove.auto_approve_no_flags_only ?? true} onCheckedChange={v => updateAA('auto_approve_no_flags_only', v)} />
                    </div>

                    <Separator />

                    {/* Document types */}
                    <div>
                      <Label>Document Types</Label>
                      <p className="text-xs text-muted-foreground mb-3">Select which document types can be auto-approved (empty = all types)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {DOC_TYPES.map(dt => (
                          <div key={dt} className="flex items-center gap-2">
                            <Checkbox checked={(autoApprove.auto_approve_document_types || []).includes(dt)} onCheckedChange={() => toggleDocType(dt)} />
                            <Label className="text-sm uppercase cursor-pointer">{dt.replace('_', ' ')}</Label>
                          </div>
                        ))}
                      </div>
                      {(autoApprove.auto_approve_document_types || []).length === 0 && (
                        <p className="text-xs text-muted-foreground mt-2">All document types are eligible for auto-approval</p>
                      )}
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3 border border-border">
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Settings auto-save. Documents with AI confidence below 70% are never auto-approved.
                      </p>
                    </div>
                  </CardContent>
                )}
              </Card>

              {!autoApprove.auto_approve_enabled && (
                <Card className="rounded-xl border-dashed">
                  <CardContent className="py-8 text-center">
                    <Zap className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">Auto-approve is disabled</p>
                    <p className="text-xs text-muted-foreground mt-1">Enable it above to automatically approve AI extractions that meet your criteria.</p>
                    <p className="text-xs text-muted-foreground mt-1">Human-in-the-loop review remains active for all documents until this is enabled.</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>


        <TabsContent value="team" className="space-y-4">
          <Card className="rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Team Members</CardTitle>
                <CardDescription>{users.length} members</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddUser(true)}><Plus className="mr-2 h-4 w-4" /> Add User</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className="hover:bg-muted/50 transition-colors duration-150">
                      <TableCell className="font-medium text-sm">{u.first_name} {u.last_name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs capitalize">{u.role}</Badge></TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteUser(u.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chase" className="space-y-4">
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Chase Configuration</CardTitle>
              <CardDescription>Configure automated vendor chasing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div><Label>Chase Enabled</Label><p className="text-xs text-muted-foreground">Enable automated compliance chasing</p></div>
                <Switch checked={settings?.chase_enabled} onCheckedChange={v => setSettings(p => ({...p, chase_enabled: v}))} />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Chase Interval (days)</Label><Input type="number" className="mt-1" value={settings?.chase_interval_days || 7}
                  onChange={e => setSettings(p => ({...p, chase_interval_days: parseInt(e.target.value)}))} /></div>
                <div><Label>Max Chase Attempts</Label><Input type="number" className="mt-1" value={settings?.max_chase_attempts || 5}
                  onChange={e => setSettings(p => ({...p, max_chase_attempts: parseInt(e.target.value)}))} /></div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>From Name</Label><Input className="mt-1" value={settings?.email_from_name || ''}
                  onChange={e => setSettings(p => ({...p, email_from_name: e.target.value}))} /></div>
                <div><Label>From Address</Label><Input className="mt-1" value="compliance@noreply.vendorsuite.io" disabled
                  readOnly /><p className="text-[10px] text-muted-foreground mt-1">Locked — all emails sent from noreply.vendorsuite.io</p></div>
              </div>
              <Button onClick={handleSaveSettings}><Save className="mr-2 h-4 w-4" /> Save Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name</Label><Input value={newUser.first_name} onChange={e => setNewUser(p => ({...p, first_name: e.target.value}))} /></div>
              <div><Label>Last Name</Label><Input value={newUser.last_name} onChange={e => setNewUser(p => ({...p, last_name: e.target.value}))} /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} /></div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                  <SelectItem value="owner">Owner (Read-only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={!newUser.email || !newUser.first_name}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NotificationToggle() {
  const [enabled, setEnabled] = useState(false);
  const [tz, setTz] = useState('America/New_York');
  const [loaded, setLoaded] = useState(false);

  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'America/Phoenix', label: 'Arizona (no DST)' },
    { value: 'UTC', label: 'UTC' },
  ];

  useEffect(() => {
    api.get('/settings/notifications').then(r => {
      setEnabled(r.data?.support_email_notifications || false);
      setTz(r.data?.timezone || 'America/New_York');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleToggle = async (value) => {
    setEnabled(value);
    try { await api.put('/settings/notifications', { support_email_notifications: value }); }
    catch { setEnabled(!value); }
  };

  const handleTzChange = async (value) => {
    setTz(value);
    try { await api.put('/settings/notifications', { timezone: value }); }
    catch {}
  };

  if (!loaded) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label>Email me when support replies to my ticket</Label>
          <p className="text-xs text-muted-foreground">Receive email notifications when the support team responds</p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggle} />
      </div>
      <Separator />
      <div>
        <Label>Timezone</Label>
        <p className="text-xs text-muted-foreground mb-2">Used to display timestamps throughout the app</p>
        <Select value={tz} onValueChange={handleTzChange}>
          <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TIMEZONES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
